import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import {
  auditLogService,
  type AuditLogEntry,
  type AuditLogFacets,
  type AuditLogFilters,
  type AuditLogPage,
} from '@services/auditLogService';
import { organizationService, type OrganizationMember } from '@services/organizationService';
import { apiErrorCode } from '@utils/apiError';
import { useFormat } from '@utils/format';

/** The URL query keys the filters live in, so a filtered view is linkable. */
const FILTER_KEYS = ['action', 'resourceType', 'userId'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

/** The API's page cap on the members list. */
const MEMBER_PAGE = 100;

type LoadState = 'ok' | 'forbidden' | 'error';

/**
 * Every member, a page at a time: anyone left out would read "Former member"
 * while still in the organization.
 */
async function fetchAllMembers(orgId: string) {
  const members: OrganizationMember[] = [];
  for (;;) {
    const page = await organizationService.getMembers(orgId, {
      limit: MEMBER_PAGE,
      offset: members.length,
    });
    members.push(...page.items);
    if (page.items.length === 0 || members.length >= page.total) return members;
  }
}

interface AuditLogTabProps {
  orgId: string;
  /** Entries per page. Tests shrink it to page through the captured trail. */
  pageSize?: number;
}

/**
 * The organization's audit trail, for owners and admins (REV-805).
 *
 * Filters and the page are in the URL, so a filtered view can be shared and
 * survives a reload; changing a filter returns to the first page.
 *
 * The filter options come from the entries themselves (GET .../facets), not a
 * fixed list, so every option matches something. Actors are named from the
 * members list; an entry by someone who has since left reads "Former member",
 * and one with no person behind it reads as an API key or the system.
 *
 * Entries show no IP address, user agent or invitee email: the API never
 * returns them.
 */
export default function AuditLogTab({ orgId, pageSize = 20 }: AuditLogTabProps) {
  const { t } = useTranslation();
  const format = useFormat();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: AuditLogFilters = useMemo(
    () => Object.fromEntries(FILTER_KEYS.map((key) => [key, searchParams.get(key) ?? undefined])),
    [searchParams]
  );
  // The API takes a whole page number; a hand-edited URL may not hold one.
  const page = Math.max(1, Math.floor(Number(searchParams.get('page'))) || 1);

  const [facets, setFacets] = useState<AuditLogFacets | null>(null);
  // Null until the members have loaded: nobody can be called a former member
  // on the strength of a request that failed.
  const [names, setNames] = useState<Map<string, string> | null>(null);
  const [result, setResult] = useState<AuditLogPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [listState, setListState] = useState<LoadState>('ok');
  const [lookupState, setLookupState] = useState<LoadState>('ok');
  const [lookupAttempt, setLookupAttempt] = useState(0);
  const latestList = useRef(0);

  const refusal = (err: unknown): LoadState =>
    apiErrorCode(err) === 'ROLE_REQUIRED' ? 'forbidden' : 'error';
  const state: LoadState = [listState, lookupState].includes('forbidden')
    ? 'forbidden'
    : [listState, lookupState].includes('error')
      ? 'error'
      : 'ok';

  useEffect(() => {
    let current = true;
    Promise.all([auditLogService.facets(orgId), fetchAllMembers(orgId)])
      .then(([found, members]) => {
        if (!current) return;
        setFacets(found);
        setLookupState('ok');
        setNames(
          new Map(
            members.map((m) => [
              m.user_id,
              `${m.user.first_name ?? ''} ${m.user.last_name ?? ''}`.trim() || m.user.email,
            ])
          )
        );
      })
      .catch((err: unknown) => {
        if (current) setLookupState(refusal(err));
      });
    return () => {
      current = false;
    };
  }, [orgId, lookupAttempt]);

  const load = useCallback(async () => {
    // Only the newest request may answer: an older, slower one would put rows
    // on screen that do not match the filters shown.
    const request = ++latestList.current;
    setLoading(true);
    try {
      const found = await auditLogService.list(orgId, page, pageSize, filters);
      if (request !== latestList.current) return;
      setResult(found);
      setListState('ok');
    } catch (err) {
      if (request !== latestList.current) return;
      // Rows from before a failed filter change would not match the filter shown.
      setResult(null);
      setListState(refusal(err));
    } finally {
      if (request === latestList.current) setLoading(false);
    }
  }, [orgId, page, pageSize, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  // A linked page past the end (the log can be filtered down since the link was
  // made): go to the last page there is, not a message that nothing is recorded.
  const pastTheEnd = !!result && result.logs.length === 0 && result.pagination.total > 0;
  const lastPage = result?.pagination.totalPages ?? 1;
  useEffect(() => {
    if (!pastTheEnd) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('page', String(lastPage));
        return next;
      },
      { replace: true }
    );
  }, [pastTheEnd, lastPage, setSearchParams]);

  const retry = () => {
    setLookupAttempt((attempt) => attempt + 1);
    void load();
  };

  const setFilter = (key: FilterKey, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setSearchParams(next);
  };

  const setPage = (zeroBased: number) => {
    const next = new URLSearchParams(searchParams);
    if (zeroBased > 0) next.set('page', String(zeroBased + 1));
    else next.delete('page');
    setSearchParams(next);
  };

  const actorName = (entry: AuditLogEntry) => {
    if (entry.userId) {
      if (!names) return t('auditLog.actor.unavailable');
      return names.get(entry.userId) ?? t('auditLog.actor.former');
    }
    const actor = entry.details?.actor as { type?: string } | undefined;
    return actor?.type === 'api_key' ? t('auditLog.actor.apiKey') : t('auditLog.actor.system');
  };

  const filtered = FILTER_KEYS.some((key) => filters[key]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" component="h2">
          {t('auditLog.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('auditLog.subtitle')}
        </Typography>
      </Box>

      {state === 'forbidden' ? (
        <Alert severity="info">{t('auditLog.forbidden')}</Alert>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <FilterSelect
              id="audit-log-action"
              label={t('auditLog.filters.action')}
              value={filters.action ?? ''}
              options={(facets?.actions ?? []).map((a) => ({ value: a, label: a }))}
              onChange={(value) => setFilter('action', value)}
            />
            <FilterSelect
              id="audit-log-resource-type"
              label={t('auditLog.filters.resourceType')}
              value={filters.resourceType ?? ''}
              options={(facets?.resourceTypes ?? []).map((r) => ({ value: r, label: r }))}
              onChange={(value) => setFilter('resourceType', value)}
            />
            <FilterSelect
              id="audit-log-user"
              label={t('auditLog.filters.user')}
              value={filters.userId ?? ''}
              options={[...(names ?? [])].map(([value, label]) => ({ value, label }))}
              onChange={(value) => setFilter('userId', value)}
            />
          </Stack>

          {state === 'error' && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={retry}>
                  {t('common.retry')}
                </Button>
              }
            >
              {t('auditLog.loadError')}
            </Alert>
          )}

          {/* Rows change in place when a filter or page does, so say so. */}
          <div role="status" style={visuallyHidden}>
            {result && !loading
              ? t('auditLog.resultCount', { count: result.pagination.total })
              : ''}
          </div>

          {(loading && !result) || pastTheEnd ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress aria-label={t('common.loading')} />
            </Box>
          ) : result && result.logs.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography>
                {filtered ? t('auditLog.emptyFiltered') : t('auditLog.empty')}
              </Typography>
            </Paper>
          ) : result ? (
            <Paper>
              <TableContainer>
                <Table size="small" aria-busy={loading}>
                  <caption style={visuallyHidden}>{t('auditLog.caption')}</caption>
                  <TableHead>
                    <TableRow>
                      {(['when', 'who', 'action', 'result', 'details'] as const).map((column) => (
                        <TableCell key={column} component="th" scope="col">
                          {t(`auditLog.columns.${column}`)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.logs.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {format.dateTime(entry.createdAt)}
                        </TableCell>
                        <TableCell>{actorName(entry)}</TableCell>
                        <TableCell>
                          <code>{entry.action}</code>
                        </TableCell>
                        <TableCell>
                          {t(`auditLog.status.${entry.status}`)}
                          {entry.errorMessage && (
                            <Typography variant="body2" color="text.secondary">
                              {entry.errorMessage}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 240 }}>
                          <Details details={entry.details} names={names} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={result.pagination.total}
                page={result.pagination.page - 1}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[]}
                onPageChange={(_, next) => setPage(next)}
              />
            </Paper>
          ) : null}
        </>
      )}
    </Box>
  );
}

interface FilterSelectProps {
  id: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}

/** One filter: "All" plus each option. */
function FilterSelect({ id, label, value, options, onChange }: FilterSelectProps) {
  const { t } = useTranslation();
  // A linked value may not be an option: facets still loading, or a former
  // member's id. It is still the filter applied, so it stays selectable.
  const shown =
    value && !options.some((option) => option.value === value)
      ? [{ value, label: value }, ...options]
      : options;
  return (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Select
        labelId={`${id}-label`}
        id={id}
        label={label}
        value={value}
        // An empty value would otherwise render a zero-width space, so "All"
        // read as no choice made (REV-580).
        displayEmpty
        renderValue={(selected) =>
          selected
            ? (shown.find((o) => o.value === selected)?.label ?? selected)
            : t('auditLog.filters.all')
        }
        onChange={(e) => onChange(e.target.value)}
      >
        <MenuItem value="">{t('auditLog.filters.all')}</MenuItem>
        {shown.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

/**
 * An entry's details as name/value pairs. Free-form per action, so shown
 * generically: an empty value or object is left out, a nested one is shown as
 * written, and a `…user_id` naming a current member shows their name.
 */
function Details({
  details,
  names,
}: {
  details: AuditLogEntry['details'];
  names: Map<string, string> | null;
}) {
  const { t } = useTranslation();
  const shown = Object.entries(details ?? {}).filter(
    ([, value]) =>
      value !== '' &&
      !(value && typeof value === 'object' && Object.keys(value as object).length === 0)
  );
  if (shown.length === 0) return null;
  return (
    <Box
      component="dl"
      sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1 }}
    >
      {shown.map(([key, value]) => (
        <Box key={key} sx={{ display: 'contents' }}>
          <Box component="dt" sx={{ color: 'text.secondary' }}>
            {key}
          </Box>
          <Box component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
            {value === null
              ? t('auditLog.details.notRecorded')
              : typeof value === 'object'
                ? JSON.stringify(value)
                : (key.endsWith('user_id') && names?.get(String(value))) || String(value)}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
